<template>
  <el-dialog
    :visible="visible"
    :title="dialogTitle"
    width="700px"
    class="goods-attr-dialog"
    @opened="afterOpen"
    @close="onClose"
  >
    <div class="scroll-wrap">
      <el-form ref="form" :model="form" label-width="130px" class="goods-attr-form">
        <!-- 属性信息区域 -->
        <div class="form-section">
          <div class="section-title">
            <i class="el-icon-setting"></i>
            <span>属性信息</span>
          </div>
          <div class="section-content">
            <el-form-item
              label="属性类别"
              prop="groupCode"
              :rules="[{ required: true, message: '属性类别不能为空' }]"
            >
              <el-select
                v-model="form.groupCode"
                placeholder="请选择属性类别"
                size="small"
                :disabled="mode === 'edit'"
                class="form-input"
              >
                <el-option
                  v-for="item in goodsGroupCodeList"
                  :key="item.value"
                  :label="item.name"
                  :value="item.value"
                ></el-option>
              </el-select>
            </el-form-item>

            <el-form-item
              label="属性值"
              prop="attrName"
              :rules="[{ required: true, message: '属性值不能为空' }]"
            >
              <el-input
                v-model="form.attrName"
                size="small"
                placeholder="请输入属性值"
                class="form-input"
              />
            </el-form-item>

            <el-form-item
              label="权重"
              prop="weight"
              :rules="[{ required: true, message: '权重不能为空' }]"
            >
              <el-input-number
                v-model="form.weight"
                size="small"
                placeholder="请输入权重"
                :min="0"
                :precision="0"
                class="form-input"
              />
            </el-form-item>
          </div>
        </div>
      </el-form>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="visible = false">取 消</el-button>
      <el-button type="primary" size="small" @click="onSubmit">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  GoodsGroupCodeToTextMap,
  GoodsTypeToGoodsGroupCodeMap,
} from "@/enum/dict/index.js";
import { addGoodsAttr, editGoodsAttr } from "./api.js";

export default {
  name: "GoodsAttrDetail",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    id: {
      type: [Number, String],
      required: false,
      default: null,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["create", "edit"].includes(value),
    },
    goodsType: {
      type: Number,
      required: false,
      default: null,
    },
    groupCode: {
      type: String,
      required: false,
      default: null,
    },
    attrName: {
      type: String,
      required: false,
      default: null,
    },
    weight: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      form: {
        id: null,
        groupCode: undefined,
        attrName: undefined,
        weight: undefined,
      },
    };
  },
  computed: {
    dialogTitle() {
      switch (this.mode) {
        case "create":
          return "新增商品属性";
        case "edit":
          return "编辑商品属性";
        default:
          return "";
      }
    },
    goodsGroupCodeList() {
      return (GoodsTypeToGoodsGroupCodeMap.get(this.goodsType) ?? []).map(
        (goodsType) => {
          return {
            name: GoodsGroupCodeToTextMap.get(goodsType),
            value: goodsType,
          };
        },
      );
    },
  },
  methods: {
    async afterOpen() {
      if (this.mode === "create") {
        this.$refs.form.resetFields();

        this.$set(this, "form", this.$options.data().form);
        return;
      }

      this.form.id = this.id;
      this.form.groupCode = this.groupCode;
      this.form.attrName = this.attrName;
      this.form.weight = this.weight;
    },
    onClose() {
      this.$emit("update:visible", false);
    },
    async onSubmit() {
      const valid = await this.$refs.form.validate();

      if (!valid) {
        return;
      }

      if (this.form.id) {
        await editGoodsAttr({
          ...this.form,
          goodsType: this.goodsType,
        });
      } else {
        await addGoodsAttr({
          ...this.form,
          goodsType: this.goodsType,
        });
      }

      this.$message({
        type: "success",
        message: "操作成功",
      });

      this.onClose();

      this.$emit("refreshList");
    },
  },
};
</script>
<style scoped lang="scss">
.goods-attr-dialog {
  ::v-deep .el-dialog {
    border-radius: 8px;
    overflow: hidden;
  }

  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
    padding: 20px 24px;
    border-bottom: none;

    .el-dialog__title {
      color: #fff;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__close {
      color: #fff;
      font-size: 20px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 24px;
    background: #f5f7fa;
  }

  ::v-deep .el-dialog__footer {
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e4e7ed;
  }

  ::v-deep .el-form-item__label {
    font-weight: 500;
    color: #606266;
    font-size: 14px;
  }

  ::v-deep .el-form-item {
    margin-bottom: 20px;
  }
}

.scroll-wrap {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.goods-attr-form {
  .form-section {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .section-title {
      background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
      padding: 14px 20px;
      border-bottom: 2px solid #e4e7ed;
      display: flex;
      align-items: center;
      font-size: 15px;
      font-weight: 600;
      color: #303133;

      i {
        margin-right: 8px;
        font-size: 16px;
        color: #409EFF;
      }
    }

    .section-content {
      padding: 20px 24px;
    }
  }

  .form-input {
    width: 100%;
    max-width: 400px;

    ::v-deep .el-input__inner {
      border-radius: 4px;
      transition: all 0.3s ease;
      text-align: left;

      &:focus {
        border-color: #409EFF;
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
      }

      &:hover {
        border-color: #c0c4cc;
      }
    }
  }

  ::v-deep .el-select {
    width: 100%;
    max-width: 400px;

    .el-input__inner {
      border-radius: 4px;
      transition: all 0.3s ease;

      &:focus {
        border-color: #409EFF;
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
      }

      &:hover {
        border-color: #c0c4cc;
      }
    }
  }

  ::v-deep .el-input-number {
    width: 100%;
    max-width: 400px;

    .el-input__inner {
      border-radius: 4px;
      transition: all 0.3s ease;
      text-align: left;

      &:focus {
        border-color: #409EFF;
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
      }

      &:hover {
        border-color: #c0c4cc;
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .el-button {
    padding: 10px 24px;
    border-radius: 4px;
    font-weight: 500;
    transition: all 0.3s ease;

    &.el-button--primary {
      background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
      border: none;

      &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
      }
    }

    &:not(.el-button--primary) {
      &:hover {
        color: #409EFF;
        border-color: #409EFF;
        background-color: #ecf5ff;
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .goods-attr-dialog {
    ::v-deep .el-dialog {
      width: 95% !important;
      margin: 5vh auto;
    }
  }

  .form-input {
    max-width: 100% !important;
  }
}
</style>
